import { useEffect, useState } from "react";
import { FileDown, Pencil, Plus, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { flagFor } from "../../lib/countryFlags";
import RateCards from "../../components/admin/RateCards";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

const SERVICE_TYPES = ["express", "dutypaid", "eco", "expedite", "self", "medicine", "document"];
const COURIERS = ["DHL", "FedEx", "UPS", "DTDC Australia", "Atlantic International", "Aramex",
            "Courier Please", "ICL Express", "SELF", "DPD", "INDIAFAST Intl", "Other"];

const newSlab = (min = 0.01, max = 0.5) => ({ min, max, type: "fixed", cost: "", price: "" });
const emptyForm = () => ({
  country: "", courier: "DHL", service_type: "express", zone: "", transit_days: "", notes: "",
  slabs: [newSlab()], active: true,
});

export default function AdminIntlRates() {
  const [tab, setTab] = useState("cards");
  const [rates, setRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [zonedMap, setZonedMap] = useState([]);
  const [zoneCountry, setZoneCountry] = useState("");
  const [zoneFile, setZoneFile] = useState(null);
  const [zoneBusy, setZoneBusy] = useState(false);
  const [selfBusy, setSelfBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [selCountries, setSelCountries] = useState({});
  const selList = Object.keys(selCountries);
  const countryList = [...new Set(rates.map((r) => r.country))].sort();
  const toggleCountry = (cname) => setSelCountries((p) => { const nx = { ...p }; if (nx[cname]) delete nx[cname]; else nx[cname] = true; return nx; });

  const load = () => api.get("/admin/intl-rates").then((r) => setRates(r.data)).catch(() => {});
  const loadZones = () => api.get("/admin/intl-zones").then((r) => setZonedMap(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadZones();
    api.get("/rates/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const uploadZones = async () => {
    if (!zoneCountry || !zoneFile) { toast.error("Select country and Excel file first"); return; }
    setZoneBusy(true);
    try {
      const fd = new FormData();
      fd.append("country", zoneCountry);
      fd.append("file", zoneFile);
      const r = await api.post("/admin/intl-zones/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.country}: ${r.data.rows} codes mapped across ${r.data.zones.length} zones`);
      setZoneFile(null); setZoneCountry(""); loadZones();
    } catch (err) { toast.error(apiError(err)); }
    finally { setZoneBusy(false); }
  };

  const delZones = async (cname) => {
    if (!window.confirm(`Delete all zipcode-zone mappings for ${cname}?`)) return;
    try { await api.delete(`/admin/intl-zones/${encodeURIComponent(cname)}`); toast.success("Zone mapping deleted"); loadZones(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const importSelf = async (f) => {
    setSelfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/intl-rates/import-self", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`SELF sheet imported — ${r.data.rates} rates (${r.data.standard} standard + ${r.data.duty_paid} duty paid) · ${r.data.zone_rows} zone codes · ${r.data.skipped.length} rows skipped`);
      load(); loadZones();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSelfBusy(false); }
  };

  const openNew = () => { setForm(emptyForm()); setEditing("new"); };
  const openEdit = (r) => {
    setForm({
      country: r.country, courier: r.courier, service_type: r.service_type, zone: r.zone || "",
      transit_days: r.transit_days || "", notes: r.notes || "",
      slabs: (r.slabs || []).map((s) => ({
        min: s.min, max: s.max, type: s.type || "fixed",
        cost: s.cost ?? "", price: s.price ?? "",
      })),
      active: r.active,
    });
    setEditing(r);
  };

  const setSlab = (i, k, v) => {
    const slabs = [...form.slabs];
    slabs[i] = { ...slabs[i], [k]: v };
    setForm({ ...form, slabs });
  };
  const addSlab = () => {
    const last = form.slabs[form.slabs.length - 1];
    const nextMin = last ? parseFloat(last.max) + 0.01 : 0.01;
    setForm({ ...form, slabs: [...form.slabs, newSlab(nextMin, nextMin + 0.49)] });
  };

  const save = async () => {
    const payload = {
      ...form,
      slabs: form.slabs.map((s) => ({
        min: parseFloat(s.min), max: parseFloat(s.max), type: s.type,
        cost: parseFloat(s.cost) || 0, price: parseFloat(s.price),
      })),
    };
    if (payload.slabs.some((s) => isNaN(s.min) || isNaN(s.max) || isNaN(s.price))) {
      toast.error("All slabs need numeric from, to and customer price");
      return;
    }
    if (form.slabs.some((s) => s.cost !== "" && isNaN(parseFloat(s.cost)))) {
      toast.error("Admin price must be a number");
      return;
    }
    try {
      if (editing === "new") await api.post("/admin/intl-rates", payload);
      else await api.put(`/admin/intl-rates/${editing.id}`, payload);
      toast.success("Rate saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (r) => {
    try { await api.put(`/admin/intl-rates/${r.id}`, { active: !r.active }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.country} ${r.courier} ${r.service_type}?`)) return;
    try { await api.delete(`/admin/intl-rates/${r.id}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const downloadPriceList = async () => {
    try {
      const res = selList.length
        ? await api.post("/admin/intl-rates.pdf", { countries: selList }, { responseType: "blob" })
        : await api.get("/admin/intl-rates.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `INDIAFAST-rate-card${selList.length ? "-" + selList.join("-") : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(selList.length ? `Price chart for ${selList.length} selected countr${selList.length > 1 ? "ies" : "y"} downloaded` : "Full price list PDF downloaded — share it on WhatsApp");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-intl-rates" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">International Rate Engine</h1>
          <p className="mt-1 text-sm text-slate-500">Country · courier · service · FIXED / PER-KG slab pricing. Admin price stays hidden — profit = customer paid − admin price.</p>
        </div>
        <div className={`flex items-center gap-2 ${tab === "cards" ? "hidden" : ""}`}>
          <label data-testid="self-import-btn"
                 className={`flex cursor-pointer items-center gap-2 rounded-full border border-adminborder px-5 py-2.5 text-sm font-bold text-slate-300 transition-colors hover:border-saffron hover:text-saffron ${selfBusy ? "pointer-events-none opacity-50" : ""}`}>
            <UploadCloud className="h-4 w-4" /> {selfBusy ? "Importing SELF sheet…" : "Import SELF Sheet"}
            <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="self-import-file"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) importSelf(f); e.target.value = ""; }} />
          </label>
          <button onClick={downloadPriceList} data-testid="intl-rates-pdf-btn"
                  className="flex items-center gap-2 rounded-full border border-saffron/50 px-5 py-2.5 text-sm font-bold text-saffron hover:bg-saffron/10">
            <FileDown className="h-4 w-4" /> {selList.length ? `Share Selected (${selList.length}) PDF` : "Share Price List PDF"}
          </button>
          <button onClick={openNew} data-testid="intl-rate-add-btn"
                  className="flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">
            <Plus className="h-4 w-4" /> Add Rate
          </button>
        </div>
      </div>

      <div className="flex gap-2" data-testid="intl-rates-tabs">
        <button onClick={() => setTab("cards")} data-testid="ratecards-tab-btn"
                className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${tab === "cards" ? "bg-saffron text-white" : "border border-adminborder text-slate-400 hover:text-white"}`}>
          Carrier Rate Cards
        </button>
        <button onClick={() => setTab("manual")} data-testid="manual-rates-tab-btn"
                className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${tab === "manual" ? "bg-saffron text-white" : "border border-adminborder text-slate-400 hover:text-white"}`}>
          Manual Country Rates
        </button>
      </div>

      {tab === "cards" && <RateCards />}

      {tab === "manual" && (<>

      {countryList.length > 0 && (
        <div className={`${card} p-4`} data-testid="rate-country-select">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Select countries to generate a combined price chart</p>
          <div className="flex flex-wrap gap-2">
            {countryList.map((cname) => (
              <label key={cname} data-testid={`rate-country-check-${cname}`}
                     className={`flex cursor-pointer items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${selCountries[cname] ? "border-saffron bg-saffron/15 text-saffron" : "border-adminborder text-slate-300 hover:border-saffron/50"}`}>
                <input type="checkbox" className="h-3.5 w-3.5 accent-saffron" checked={!!selCountries[cname]} onChange={() => toggleCountry(cname)} />
                {flagFor(cname)} {cname}
              </label>
            ))}
            {selList.length > 0 && (
              <button onClick={() => setSelCountries({})} data-testid="rate-country-clear"
                      className="rounded-full border border-adminborder px-3.5 py-1.5 text-xs font-bold text-slate-400 hover:border-red-500 hover:text-red-400">
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className={`${card} p-4`} data-testid="zone-manager">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Country Zones — zipcode → zone Excel upload (Australia · New Zealand · Canada)</p>
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${darkInput} max-w-56`} data-testid="zone-country-select" value={zoneCountry} onChange={(e) => setZoneCountry(e.target.value)}>
            <option value="">Select country…</option>
            {countries.map((c) => <option key={c} value={c}>{flagFor(c)} {c}</option>)}
          </select>
          <input type="file" accept=".xlsx,.xls" data-testid="zone-file-input"
                 onChange={(e) => setZoneFile(e.target.files?.[0] || null)}
                 className="max-w-64 text-xs text-slate-400 file:mr-2 file:rounded-lg file:border-0 file:bg-adminbg file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200" />
          <button onClick={uploadZones} disabled={zoneBusy || !zoneCountry || !zoneFile} data-testid="zone-upload-btn"
                  className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
            {zoneBusy ? "Uploading…" : "Upload Zones"}
          </button>
        </div>
        {zonedMap.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="zone-chip-list">
            {zonedMap.map((z) => (
              <span key={z.country} data-testid={`zone-chip-${z.country}`}
                    className="flex items-center gap-2 rounded-full border border-adminborder bg-adminbg px-3 py-1.5 text-xs font-bold text-slate-200">
                {flagFor(z.country)} {z.country} · {z.zones.length} zones · {z.rows} codes
                <button onClick={() => delZones(z.country)} data-testid={`zone-delete-${z.country}`} aria-label={`Delete ${z.country} zones`}
                        className="text-red-400 hover:text-red-300"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        <p className="mt-2 text-[10px] text-slate-500">Excel columns: <b>Zipcode + Zone</b> (one code per row) or <b>Zipcode From + Zipcode To + Zone</b> (ranges). Canada FSA prefixes (e.g. M5V) supported. Re-upload replaces that country's list. Zone-wise rate cards: pick the zone in the Add Rate form.</p>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="intl-rates-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Country</th><th className="px-5 py-3.5">Courier</th><th className="px-5 py-3.5">Service</th>
              <th className="px-5 py-3.5">Transit</th><th className="px-5 py-3.5">Slabs</th><th className="px-5 py-3.5">Pricing</th>
              <th className="px-5 py-3.5">Active</th><th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => {
              const perKg = (r.slabs || []).filter((s) => s.type === "per_kg").length;
              const fixed = (r.slabs || []).length - perKg;
              return (
                <tr key={r.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5">
                  <td className="px-5 py-3.5 font-bold text-white"><span className="mr-1.5">{flagFor(r.country)}</span>{r.country}
                    {r.zone && <span className="ml-2 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-400" data-testid={`intl-rate-zone-${r.id}`}>ZONE {r.zone}</span>}
                  </td>
                  <td className="px-5 py-3.5">{r.courier}</td>
                  <td className="px-5 py-3.5 capitalize">{r.service_type}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-400">{r.transit_days || "—"}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-400">
                    {(r.slabs || []).length} slabs · up to {r.slabs?.length ? r.slabs[r.slabs.length - 1].max : 0} kg
                  </td>
                  <td className="px-5 py-3.5 text-xs">
                    {fixed > 0 && <span className="mr-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 font-bold text-emerald-400">{fixed} FIXED</span>}
                    {perKg > 0 && <span className="rounded-full bg-red-500/15 px-2 py-0.5 font-bold text-red-400">{perKg} PER KG</span>}
                    {(r.slabs || []).some((s) => Number(s.cost) === Number(s.price)) && (
                      <span className="ml-1.5 rounded-full bg-amber-500/15 px-2 py-0.5 font-bold text-amber-400" data-testid={`intl-rate-costwarn-${r.id}`}>SET ADMIN ₹</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => toggle(r)} data-testid={`intl-rate-toggle-${r.id}`}
                            className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${r.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                      {r.active ? "ACTIVE" : "OFF"}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(r)} data-testid={`intl-rate-edit-${r.id}`} className="rounded-lg border border-adminborder p-2 text-slate-300 hover:border-saffron hover:text-saffron"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(r)} data-testid={`intl-rate-del-${r.id}`} className="rounded-lg border border-adminborder p-2 text-red-400 hover:border-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rates.length && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500">No international rates yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="intl-rate-modal">
          <div className={`${card} mx-auto my-8 w-full max-w-2xl p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">{editing === "new" ? "Add International Rate" : `Edit — ${editing.country} ${editing.courier}`}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <input className={darkInput} data-testid="rate-country" placeholder="Country — type to search (e.g. USA)" list="rate-country-list" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              <datalist id="rate-country-list" data-testid="rate-country-list">
                {countries.map((c) => <option key={c} value={c} />)}
              </datalist>
              {zonedMap.some((z) => z.country === form.country) && (
                <select className={darkInput} data-testid="rate-zone" value={form.zone}
                        onChange={(e) => setForm({ ...form, zone: e.target.value })}>
                  <option value="">Zone — select…</option>
                  {zonedMap.find((z) => z.country === form.country).zones.map((z) => <option key={z} value={z}>Zone {z}</option>)}
                </select>
              )}
              <select className={darkInput} data-testid="rate-courier" value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })}>
                {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select className={darkInput} data-testid="rate-service-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
                {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className={darkInput} data-testid="rate-transit" placeholder="Transit days (e.g. 4-6 days)" value={form.transit_days} onChange={(e) => setForm({ ...form, transit_days: e.target.value })} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="rate-notes" placeholder="Notes (customs, documents required…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <p className="mb-2 mt-5 text-xs font-bold uppercase tracking-wider text-slate-500">Weight Slabs</p>
            <div className="grid grid-cols-12 gap-2 px-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
              <span className="col-span-2">From kg</span><span className="col-span-2">To kg</span>
              <span className="col-span-3">Type</span><span className="col-span-2">Admin ₹</span>
              <span className="col-span-2">Customer ₹</span><span className="col-span-1"></span>
            </div>
            <div className="mt-1.5 space-y-2">
              {form.slabs.map((s, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`slab-row-${i}`}>
                  <input type="number" step="0.01" className={`${darkInput} col-span-2`} data-testid={`slab-${i}-min`} placeholder="0.01" value={s.min} onChange={(e) => setSlab(i, "min", e.target.value)} />
                  <input type="number" step="0.01" className={`${darkInput} col-span-2`} data-testid={`slab-${i}-max`} placeholder="0.5" value={s.max} onChange={(e) => setSlab(i, "max", e.target.value)} />
                  <div className="col-span-3 flex rounded-lg border border-adminborder p-0.5" data-testid={`slab-${i}-type`}>
                    <button type="button" onClick={() => setSlab(i, "type", "fixed")} data-testid={`slab-${i}-type-fixed`}
                            className={`flex-1 rounded-md px-1 py-1.5 text-[10px] font-bold transition-colors ${s.type === "fixed" ? "bg-emerald-500/20 text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>
                      FIXED
                    </button>
                    <button type="button" onClick={() => setSlab(i, "type", "per_kg")} data-testid={`slab-${i}-type-perkg`}
                            className={`flex-1 rounded-md px-1 py-1.5 text-[10px] font-bold transition-colors ${s.type === "per_kg" ? "bg-red-500/20 text-red-400" : "text-slate-500 hover:text-slate-300"}`}>
                      PER KG
                    </button>
                  </div>
                  <input type="number" className={`${darkInput} col-span-2`} data-testid={`slab-${i}-cost`} placeholder="Admin ₹" value={s.cost} onChange={(e) => setSlab(i, "cost", e.target.value)} />
                  <input type="number" className={`${darkInput} col-span-2`} data-testid={`slab-${i}-price`} placeholder="Customer ₹" value={s.price} onChange={(e) => setSlab(i, "price", e.target.value)} />
                  <button onClick={() => setForm({ ...form, slabs: form.slabs.filter((_, j) => j !== i) })} data-testid={`slab-${i}-del`} className="col-span-1 text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={addSlab} data-testid="slab-add-btn" className="mt-2 flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
              <Plus className="h-3.5 w-3.5" /> Add Slab
            </button>
            <p className="mt-2 text-[11px] text-slate-500">
              FIXED = flat charge when shipment weight falls in the range · PER KG = amount × total chargeable weight.
              Admin price is never shown to customers or agents — it is used only for profit calculation (profit = customer paid − admin price).
            </p>

            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rate-active" /> Active
            </label>

            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={save} data-testid="rate-save-btn" className="rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover">Save Rate</button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
